import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authService } from '../../services/authService';
import { loginSuccess } from '../../store/authSlice';
import './Login.css';

const Login = ({ authError }) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [loginError, setLoginError] = useState('');

    // MFA State
    const [isMfaRequired, setIsMfaRequired] = useState(false);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [mfaVerificationId, setMfaVerificationId] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [isMfaCodeSent, setIsMfaCodeSent] = useState(false);

    // Forgot Password State
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [resetSent, setResetSent] = useState(false);

    useEffect(() => {
        // Initialize Recaptcha with a fresh instance
        try {
            authService.initRecaptcha('recaptcha-container');
        } catch (e) {
            console.warn('Recaptcha init error:', e);
        }

        const handleAuthChange = () => {
            // ...
            setIsSigningIn(false);
        };

        window.addEventListener('authStatusChanged', handleAuthChange);
        return () => window.removeEventListener('authStatusChanged', handleAuthChange);
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsSigningIn(true);
        setLoginError(null);

        try {
            const result = await authService.loginWithEmail(email, password);

            if (result && result.mfaRequired) {
                setIsMfaRequired(true);
                setMfaResolver(result.resolver);
                setIsSigningIn(false);
            } else if (result) {
                dispatch(loginSuccess({ user: result.user, permissions: result.permissions }));
            }
        } catch (error) {
            console.error('Sign in failed:', error);
            setLoginError(error.message || 'Sign in failed. Please try again.');
            setIsSigningIn(false);
        }
    };

    const handleSendMFACode = async () => {
        setIsSigningIn(true);
        try {
            const vId = await authService.sendMFACode(mfaResolver);
            setMfaVerificationId(vId);
            setIsMfaCodeSent(true);
            setIsSigningIn(false);
        } catch (error) {
            setLoginError(error.message || 'Failed to send MFA code');
            setIsSigningIn(false);
        }
    };

    const handleVerifyMFACode = async (e) => {
        e.preventDefault();
        setIsSigningIn(true);
        try {
            const result = await authService.verifyMFACode(mfaResolver, mfaVerificationId, mfaCode);
            dispatch(loginSuccess({ user: result.user, permissions: result.permissions }));
        } catch (error) {
            setLoginError(error.message || 'Invalid MFA code');
            setIsSigningIn(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setIsSigningIn(true);
        setLoginError(null);
        try {
            await authService.forgotPassword(email);
            setResetSent(true);
            setIsSigningIn(false);
        } catch (error) {
            setLoginError(error.message || 'Failed to send reset email');
            setIsSigningIn(false);
        }
    };

    const renderContent = () => {
        if (isForgotPassword) {
            return (
                <div className="login-card">
                    <h1>Reset Password</h1>
                    {resetSent ? (
                        <div className="success-message">
                            Reset email sent! Check your inbox.
                            <button className="link-btn" onClick={() => setIsForgotPassword(false)}>Back to Login</button>
                        </div>
                    ) : (
                        <form onSubmit={handleForgotPassword}>
                            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                            {loginError && <div className="error-message">{loginError}</div>}
                            <button type="submit" disabled={isSigningIn}>{isSigningIn ? 'Sending...' : 'Send Reset Link'}</button>
                            <button type="button" className="link-btn" onClick={() => setIsForgotPassword(false)}>Back to Login</button>
                        </form>
                    )}
                </div>
            );
        }

        if (isMfaRequired) {
            return (
                <div className="login-card">
                    <h1>Two-Factor Authentication</h1>
                    <p>Verify your identity</p>
                    {!isMfaCodeSent ? (
                        <>
                            <button onClick={handleSendMFACode} disabled={isSigningIn}>
                                {isSigningIn ? 'Sending...' : 'Send Code'}
                            </button>
                            {/* recaptcha-container is now global below */}
                        </>
                    ) : (
                        <form onSubmit={handleVerifyMFACode}>
                            <input type="text" placeholder="Enter Code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required />
                            {loginError && <div className="error-message">{loginError}</div>}
                            <button type="submit" disabled={isSigningIn}>{isSigningIn ? 'Verifying...' : 'Verify'}</button>
                        </form>
                    )}
                    <button className="link-btn" onClick={() => window.location.reload()}>Cancel</button>
                </div>
            );
        }

        return (
            <div className="login-card">
                <h1>Maximal</h1>

                {(authError || loginError) && (
                    <div className="error-message">
                        {authError || loginError}
                    </div>
                )}

                <form onSubmit={handleLogin}>
                    <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="submit" disabled={isSigningIn || loading}>
                        {isSigningIn ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>

                <button className="link-btn" onClick={() => setIsForgotPassword(true)}>Forgot Password?</button>

                <p>Use your organization account to sign in.</p>
            </div>
        );
    };

    return (
        <div className="login-container">
            {renderContent()}
            <div id="recaptcha-container"></div>
        </div>
    );
};

export default Login;